import type { FieldDefinition, FieldType } from "~/models/Automation";

export interface FieldModifier {
    name: string;
    arguments: string[];
}

export interface FieldExpression {
    key: string;
    modifiers: FieldModifier[];
}

export function fieldTokenPattern(prefix = "{{", suffix = "}}"): string {
    const escape = (value: string) =>
        value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const start = escape(prefix);
    const end = escape(suffix);
    return `${start}((?:"[^"]*"|'[^']*'|(?!${start}|${end})[^"'])*)${end}`;
}

function splitExpression(
    input: string,
    delimiter: string,
): string[] | undefined {
    const parts: string[] = [];
    let start = 0;
    let depth = 0;
    let quote = "";

    for (let i = 0; i < input.length; i++) {
        const char = input[i];
        if (quote) {
            if (char === quote) quote = "";
            continue;
        }
        if (input.startsWith("{{", i) || input.startsWith("}}", i))
            return undefined;
        if (char === '"' || char === "'") {
            quote = char;
        } else if (char === "(") {
            depth++;
        } else if (char === ")") {
            if (--depth < 0) return undefined;
        } else if (char === delimiter && depth === 0) {
            parts.push(input.slice(start, i).trim());
            start = i + 1;
        }
    }

    if (quote || depth) return undefined;
    parts.push(input.slice(start).trim());
    return parts;
}

function parseModifier(segment: string): FieldModifier | undefined {
    const colon = segment.indexOf(":");
    const name = (colon < 0 ? segment : segment.slice(0, colon)).trim();
    if (!/^[a-z_][a-z0-9_]*$/.test(name)) return undefined;
    const args = colon < 0 ? "" : segment.slice(colon + 1).trim();
    const values = args ? splitExpression(args, ",") : [];
    if (!values || values.some((value) => !value)) return undefined;
    return { name, arguments: values };
}

export function parseFieldExpression(
    input: string,
): FieldExpression | undefined {
    const segments = splitExpression(input, "|");
    const key = segments?.shift();
    if (!key) return undefined;
    const modifiers: FieldModifier[] = [];
    for (const segment of segments ?? []) {
        const modifier = parseModifier(segment);
        if (!modifier) return undefined;
        modifiers.push(modifier);
    }
    return { key, modifiers };
}

export function findExpressionField<
    T extends Pick<FieldDefinition, "key"> & { aliases?: string[] },
>(fields: T[], expression: string): T | undefined {
    const parsed = parseFieldExpression(expression);
    if (!parsed) return undefined;
    const field = fields.find(
        (item) => item.key === parsed.key || item.aliases?.includes(parsed.key),
    );
    return field ? { ...field, key: expression } : undefined;
}

export function expressionLiteral(
    key: string,
): { value: string | number | boolean; type: FieldType } | undefined {
    if (/^("[^"]*"|'[^']*')$/s.test(key))
        return { value: key.replace(/^["']+|["']+$/g, ""), type: "string" };
    if (/^(true|false)$/i.test(key))
        return { value: key.toLowerCase() === "true", type: "bool" };
    if (
        /^[+-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:e[+-]?[0-9]+)?$/i.test(key) &&
        Number.isFinite(Number(key))
    )
        return { value: Number(key), type: "number" };
    return undefined;
}
